'use client';

import * as React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Dropzone, {
  type DropzoneProps,
  type FileRejection,
} from 'react-dropzone';

import { Button, Progress } from '@nextui-org/react';
import { PlusIcon, UploadIcon, X } from 'lucide-react';
import Image from 'next/image';

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { FormLabel } from '@/components/ui/form';
import { IMasterclassImage } from '@/entities/masterclass/types';
import { useControllableState } from '@/shared/hooks/use-controllable-state';
import { cn, formatBytes } from '@/shared/utils/utils';
import { useToast } from '@/components/ui/use-toast';

export interface FileUploaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Value of the uploader.
   * @type File[]
   * @default undefined
   * @example value={files}
   */
  value?: (File | IMasterclassImage)[];

  label: string;

  /**
   * Function to be called when the value changes.
   * @type (files: File[]) => void
   * @default undefined
   * @example onValueChange={(files) => setFiles(files)}
   */
  onValueChange?: (_files: (File | IMasterclassImage)[]) => void;

  /**
   * Function to be called when files are uploaded.
   * @type (files: File[]) => Promise<void>
   * @default undefined
   * @example onUpload={(files) => uploadFiles(files)}
   */
  onUpload?: (_files: File[]) => Promise<void>;

  /**
   * Progress of the uploaded files.
   * @type Record<string, number> | undefined
   * @default undefined
   * @example progresses={{ "file1.png": 50 }}
   */
  progresses?: Record<string, number>;

  /**
   * Accepted pdf types for the uploader.
   * @type { [key: string]: string[]}
   * @default
   * ```ts
   * { "image/*": [] }
   * ```
   * @example accept={["image/png", "image/jpeg"]}
   */
  accept?: DropzoneProps['accept'];

  /**
   * Maximum pdf size for the uploader.
   * @type number | undefined
   * @default 1024 * 1024 * 2 // 2MB
   * @example maxSize={1024 * 1024 * 2} // 2MB
   */
  maxSize?: DropzoneProps['maxSize'];

  /**
   * Maximum number of files for the uploader.
   * @type number | undefined
   * @default 1
   * @example maxFileCount={4}
   */
  maxFileCount?: DropzoneProps['maxFiles'];

  /**
   * Whether the uploader should accept multiple files.
   * @type boolean
   * @default false
   * @example multiple
   */
  multiple?: boolean;

  /**
   * Whether the uploader is disabled.
   * @type boolean
   * @default false
   * @example disabled
   */
  disabled?: boolean;
  isModal?: boolean;
}

export function FileUploader(props: FileUploaderProps) {
  const {
    value: valueProp,
    onValueChange,
    onUpload,
    progresses,
    accept = {
      'image/*': [],
    },
    maxSize = 1024 * 1024 * 2,
    maxFileCount = 1,
    multiple = false,
    disabled = false,
    className,
    isModal = false,
    ...dropzoneProps
  } = props;

  const [isUploadDrawerOpen, setIsUploadDrawerOpen] = React.useState(false);

  const [files, setFiles] = useControllableState<(File | IMasterclassImage)[]>({
    prop: valueProp,
    onChange: onValueChange,
  });
  const { toast } = useToast();

  const handleOnDragEnd = (result: any) => {
    // eslint-disable-next-line no-console
    console.log('handleOnDragEnd', result);
    if (!result.destination) return;
    // @ts-ignore
    const items = Array.from(files);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFiles(items);
  };

  const onDrop = React.useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // eslint-disable-next-line no-console
      console.log('onDrop', acceptedFiles, rejectedFiles);
      if (!multiple && maxFileCount === 1 && acceptedFiles.length > 1) {
        toast({
          title: 'Нельзя загрузить более 1 файла',
          variant: 'error',
        });
        return;
      }

      if ((files?.length ?? 0) + acceptedFiles.length > maxFileCount) {
        toast({
          title: `Нельзя загрузить более ${maxFileCount} файлов`,
          variant: 'error',
        });
        return;
      }

      if ((files?.length ?? 0) + rejectedFiles.length > maxFileCount) {
        toast({
          title: `Нельзя загрузить более ${maxFileCount} файлов`,
          variant: 'error',
        });
        return;
      }

      if (rejectedFiles.length > 0) {
        toast({
          title: rejectedFiles[0].errors[0].message,
          variant: 'error',
        });
      }

      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );

      const updatedFiles = files ? [...files, ...newFiles] : newFiles;

      setFiles(updatedFiles);

      if (
        onUpload &&
        updatedFiles.length > 0 &&
        updatedFiles.length <= maxFileCount
      ) {
        // toast.promise(onUpload(updatedFiles), {
        //   loading: `Uploading ${target}...`,
        //   success: () => {
        //     setFiles([]);
        //     return `${target} uploaded`;
        //   },
        //   error: `Failed to upload ${target}`,
        // });
      }
      setIsUploadDrawerOpen(false);
    },

    [files, maxFileCount, multiple, onUpload, setFiles],
  );

  function onRemove(index: number) {
    if (!files) return;
    const newFiles = files.filter(
      (_: File | IMasterclassImage, i: number) => i !== index,
    );
    setFiles(newFiles);
    onValueChange?.(newFiles);
  }

  // Revoke preview url when component unmounts
  React.useEffect(() => {
    return () => {
      if (!files) return;
      files.forEach((file: File | IMasterclassImage) => {
        if (isFileWithPreview(file)) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisabled = disabled || (files?.length ?? 0) >= maxFileCount;

  const dropzone = (
    <>
      <Dropzone
        onDrop={onDrop}
        accept={accept}
        maxSize={maxSize}
        maxFiles={maxFileCount}
        multiple={maxFileCount > 1 || multiple}
        disabled={isDisabled}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={cn(
              'group relative grid h-52 w-full cursor-pointer place-items-center rounded-lg border-2 border-dashed border-muted-foreground/25 px-5 py-2.5 text-center transition hover:bg-muted/25',
              'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isDragActive && 'border-muted-foreground/50',
              isDisabled && 'pointer-events-none opacity-60',
              className,
            )}
            {...dropzoneProps}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                <div className="rounded-full border border-dashed p-3">
                  <UploadIcon
                    className="size-7 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
                <p className="font-medium text-muted-foreground">
                  Drop the files here
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                <div className="rounded-full border border-dashed p-3">
                  <UploadIcon
                    className="size-7 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
                <div className="flex flex-col gap-px">
                  <p className="font-medium text-muted-foreground">
                    Перетащите файлы сюда или нажмите для загрузки
                  </p>
                  <p className="text-sm text-muted-foreground/70">
                    Ты можешь загрузить
                    {maxFileCount > 1
                      ? ` ${maxFileCount === Infinity ? 'multiple' : maxFileCount - (files?.length ?? 0)}
                      файлов (max размер файла ${formatBytes(maxSize)})`
                      : `max размер файла ${formatBytes(maxSize)}`}
                  </p>
                  {/*<p className="text-sm text-muted-foreground/70">*/}
                  {/*  Файлы формата {Object.keys(accept).join(', ')}*/}
                  {/*</p>*/}
                </div>
              </div>
            )}
          </div>
        )}
      </Dropzone>
    </>
  );

  const dragContent = (
    <>
      {files?.length ? (
        <>
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="droppable" direction="horizontal">
              {(provided, snapshot) => (
                <div
                  className={`${snapshot.isDraggingOver ? 'border-blue-500/50 bg-green-100' : 'bg-muted/5 border-muted-foreground/25'}
                   overflow-auto flex rounded-md gap-3 border-1  p-3
                  `}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {files.map((item: File | IMasterclassImage, index) => (
                    <Draggable
                      key={item.name}
                      draggableId={item.name}
                      index={index}
                    >
                      {(provided, _snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <FileCard
                            file={item}
                            onRemove={() => onRemove(index)}
                            progress={progresses?.[item.name]}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      ) : null}
    </>
  );

  if (isModal) {
    return (
      <div className="w-full flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <FormLabel>{props.label}</FormLabel>
          <Button
            type="button"
            isIconOnly
            onPress={() => setIsUploadDrawerOpen(true)}
            radius="full"
            variant="bordered"
          >
            <PlusIcon size={28} strokeWidth={1.25} />
          </Button>
        </div>

        <Drawer
          open={isUploadDrawerOpen}
          onOpenChange={setIsUploadDrawerOpen}
          shouldScaleBackground={false}
        >
          <DrawerContent
            overlayProps={{
              onPointerDown: () => setIsUploadDrawerOpen(false),
            }}
          >
            <DrawerHeader className="shrink-0 border-b border-default-200 text-left">
              <DrawerTitle className="text-xl font-semibold">
                Загрузка файлов
              </DrawerTitle>
            </DrawerHeader>
            <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-4 pb-8 [-webkit-overflow-scrolling:touch]">
              {dropzone}
            </div>
          </DrawerContent>
        </Drawer>
        {dragContent}
      </div>
    );
  }

  return (
    <div className="w-full relative flex flex-col gap-6 overflow-hidden">
      {dropzone}
      {dragContent}
    </div>
  );
}

interface FileCardProps {
  file: File | IMasterclassImage;
  onRemove: () => void;
  progress?: number;
}

/**
 * Размер файла в байтах. Браузерный File хранит size в байтах,
 * а медиа из Strapi — в килобайтах (size), поэтому переводим в байты.
 */
function fileSizeInBytes(file: File | IMasterclassImage): number {
  // Медиа из Strapi определяем по полю mime (у браузерного File — type).
  const isStrapiMedia = 'mime' in file && !('type' in file);
  const anyFile = file as { sizeInBytes?: number; size?: number };
  if (isStrapiMedia) {
    if (typeof anyFile.sizeInBytes === 'number') return anyFile.sizeInBytes;
    return (anyFile.size ?? 0) * 1024; // Strapi хранит size в КБ
  }
  return anyFile.size ?? 0; // браузерный File — size в байтах
}

function FileCard({ file, progress, onRemove }: FileCardProps) {
  return (
    <div className="relative flex flex-col items-center gap-2.5">
      {isFileWithPreview(file) ? <FilePreview file={file} /> : null}

      <>
        {isPdf(file) && (
          <div className="flex w-full max-w-[100px] flex-col gap-2">
            <div className="flex flex-col gap-px">
              <p className="line-clamp-1 text-wrap text-sm font-medium text-foreground/80">
                {file.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatBytes(fileSizeInBytes(file))}
              </p>
            </div>
            {progress ? <Progress value={progress} /> : null}
          </div>
        )}
      </>

      <div className="flex items-center gap-2 top-0 right-0 absolute">
        <Button
          type="button"
          size="sm"
          radius="full"
          isIconOnly
          color="danger"
          variant="bordered"
          onClick={onRemove}
        >
          <X className="size-4 accent-red-500" aria-hidden="true" />
          <span className="sr-only">Remove file</span>
        </Button>
      </div>
    </div>
  );
}

function isFileWithPreview(
  file: File | IMasterclassImage,
): file is File & { preview: string } {
  return (
    ('preview' in file && typeof file.preview === 'string') || 'url' in file
  );
}

interface FilePreviewProps {
  file: (File & { preview: string }) | IMasterclassImage;
}

const isImage = (file: File | IMasterclassImage): file is File => {
  return (
    ('type' in file && file.type.startsWith('image/')) ||
    ('mime' in file && file.mime.startsWith('image/'))
  );
};

const isPdf = (file: File | IMasterclassImage): file is File => {
  return (
    ('type' in file && file.type.startsWith('application/pdf')) ||
    ('mime' in file && file.mime.startsWith('application/pdf'))
  );
};

function FilePreview({ file }: FilePreviewProps) {
  // @ts-ignore
  if (isImage(file)) {
    return (
      <div className="w-[150px] h-[150px]">
        <Image
          // @ts-ignore
          src={file.preview ?? file.url}
          alt={file.name}
          width={150}
          height={150}
          loading="lazy"
          className="aspect-square border shrink-0 rounded-md object-cover"
        />
      </div>
    );
  }

  if (isPdf(file)) {
    return (
      <Image
        src={'/icon/pdf.svg'}
        alt={file.name}
        width={100}
        height={120}
        loading="lazy"
        className="aspect-square border shrink-0 rounded-md object-cover"
      />
    );
  }

  return <div>Неизвестный тип файла</div>;
}
